import { format, subMonths, subYears, startOfYear, endOfYear } from "date-fns";

import { getOrganizationCarbonEmissions } from "Api/carbonEmissions/getOrganizationCarbonEmissions.request";
import { getOrganizationId } from "Libs/utils";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { setOrganizationLoading } from "../carbonEmissions.reducer";
import { CarbonEmissionsTimeframe } from "../normalizeInput";

// Define a custom error payload type that includes organizationId
interface ErrorPayload {
  error: string;
  organizationId?: string;
}

const createErrorPayload = (error: unknown): ErrorPayload => ({
  error: error instanceof Error ? error.message : "An unknown error occurred"
});

// Helper function to format dates for API
const formatDate = (date: Date): string => format(date, "yyyy-MM-dd");

type FetchCarbonEmissionsThunkArgs = {
  organizationName: string;
  from?: string;
  to?: string;
  interval?: "day" | "week" | "month";
};

export const fetchOrganizationCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchOrganizationCarbonEmissions",
  async (
    args: FetchCarbonEmissionsThunkArgs,
    { rejectWithValue, getState }
  ) => {
    try {
      const { organizationName, from, to, interval } = args;

      // Get organizationId from organizationName using the utility function
      const organizationId = getOrganizationId(getState, organizationName);

      if (!organizationId) {
        throw new Error(
          `Could not find organization ID for ${organizationName}`
        );
      }

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from: from || formatDate(subMonths(new Date(), 6)),
        to: to || formatDate(new Date()),
        interval: interval || "month"
      });

      return {
        organizationId,
        data: response
      };
    } catch (error) {
      return rejectWithValue(createErrorPayload(error));
    }
  }
);

export const fetchPreviousYearCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchPreviousYearCarbonEmissions",
  async (organizationName: string, { rejectWithValue, getState }) => {
    try {
      // Get organizationId from organizationName using the utility function
      const organizationId = getOrganizationId(getState, organizationName);

      if (!organizationId) {
        throw new Error(
          `Could not find organization ID for ${organizationName}`
        );
      }

      const now = new Date();
      const lastYear = subYears(now, 1);
      const from = formatDate(startOfYear(lastYear));
      const to = formatDate(endOfYear(lastYear));

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      return {
        organizationId, // use organizationId not organizationName for state
        data: response,
        timeframe: "previousYear" as CarbonEmissionsTimeframe
      };
    } catch (error) {
      return rejectWithValue(createErrorPayload(error));
    }
  }
);

export const fetchCurrentYearCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchCurrentYearCarbonEmissions",
  async (organizationName: string, { rejectWithValue, getState }) => {
    try {
      // Get organizationId from organizationName using the utility function
      const organizationId = getOrganizationId(getState, organizationName);

      if (!organizationId) {
        throw new Error(
          `Could not find organization ID for ${organizationName}`
        );
      }

      const now = new Date();
      const from = formatDate(startOfYear(now));
      const to = formatDate(now);

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      return {
        organizationId, // use organizationId not organizationName for state
        data: response,
        timeframe: "currentYear" as CarbonEmissionsTimeframe
      };
    } catch (error) {
      return rejectWithValue(createErrorPayload(error));
    }
  }
);

export const fetchLastSixMonthsCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchLastSixMonthsCarbonEmissions",
  async (organizationName: string, { rejectWithValue, getState, dispatch }) => {
    try {
      // Get organizationId from organizationName
      const organizationId = getOrganizationId(getState, organizationName);

      if (!organizationId) {
        throw new Error(
          `Could not find organization ID for ${organizationName}`
        );
      }

      // Set loading state
      dispatch(
        setOrganizationLoading({
          organizationId,
          isLoading: true
        })
      );

      // Get dates for past 7 months to ensure we have 6 months of data
      const now = new Date();
      const sevenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 7, 1);
      const from = formatDate(sevenMonthsAgo);
      const to = formatDate(now);

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      // Check if response has valid structure
      if (!response || !response.projects || response.projects.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("API returned empty or invalid carbon emissions data");
      }

      return {
        organizationId,
        data: response,
        timeframe: "lastSixMonths" as CarbonEmissionsTimeframe
      };
    } catch (error) {
      // If we have an organizationId, include it in the error payload and set loading to false
      const organizationId = getOrganizationId(getState, organizationName);

      // Set loading to false if we have an ID
      if (organizationId) {
        dispatch(
          setOrganizationLoading({
            organizationId,
            isLoading: false
          })
        );
      }

      const errorPayload = createErrorPayload(error);
      if (organizationId) {
        errorPayload.organizationId = organizationId;
      }

      return rejectWithValue(errorPayload);
    }
  }
);

export const fetchAllOrganizationCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchAllOrganizationCarbonEmissions",
  async (organizationName: string, { dispatch, rejectWithValue, getState }) => {
    try {
      // Get organizationId from organizationName using the utility function
      const organizationId = getOrganizationId(getState, organizationName);

      if (!organizationId) {
        throw new Error(
          `Could not find organization ID for ${organizationName}`
        );
      }

      await Promise.all([
        dispatch(fetchPreviousYearCarbonEmissionsThunk(organizationName)),
        dispatch(fetchCurrentYearCarbonEmissionsThunk(organizationName)),
        dispatch(fetchLastSixMonthsCarbonEmissionsThunk(organizationName))
      ]);

      return { success: true };
    } catch (error) {
      return rejectWithValue(createErrorPayload(error));
    }
  }
);
